import jexl from "jexl";
import { DateTime } from 'luxon';

import {
  InternalApplicationStatuses,
  ProgramStatuses,
  EventStatuses,
} from "../common/constants/statuses";
import { get } from "lodash";
import { URL_SEARCH_PARAMS } from "../common/constants/urlParams";
import { SettingNames } from "../programSettings/enums";
import {
  ALLOWED_REDIRECT_HOSTS,
  ALLOWED_REDIRECT_HOST_SUFFIXES,
  DOMAIN_KEY_HEADER_NAME,
} from "../common/constants/domainKeyRedirect";

export const USER_APPLICATION_SESSION_KEY = "userApplicationData";
export const DOMAIN_KEY = "domainKey";

export const envType = process.env.REACT_APP_TYPE ?? "beta";

export const VERIFICATION_ATTEMPT_KEY = "verificationAttempted";
export const VERIFICATION_STATUS_KEY = "lastVerificationStatus";
export const VERIFICATION_ATTEMPTED_VALUE = "true";
export const PROXY_SESSION_ID_KEY = "X-Proxy-Session-Id";

const isLocalRedirectHost = (host) => {
  if (!host) {
    return false;
  }

  return ALLOWED_REDIRECT_HOSTS.includes(host.toLowerCase());
};

// Retrieves a nested value from an object using a dot-separated path.
// Example: getFieldMappingValue({a: {b: 1}}, "a.b") returns 1.
export const getFieldMappingValue = (obj, path) => {
  if (!path || !obj) return undefined;

  // Split the path into keys, reverse and traverse the object
  return path
    .split(".")
    .reverse()
    .reduce(
      (acc, key) => (acc && typeof acc === "object" ? acc[key] : undefined),
      obj,
    );
};

const extractDomainKey = () => {
  // First check subdomain for a domain key.
  const hostname = window.location.hostname;
  const parts = hostname.split(".");

  const paymentIndex = parts.indexOf("payments");
  // Payment index is greater than 0 when a subdomain key is present
  if (paymentIndex > 0) {
    // Gets domain key from subdomain (before payments)
    const domainKey = parts[paymentIndex - 1];
    // Ignore if in a test environment
    if (
      domainKey?.length >= 3 &&
      domainKey !== "dev" &&
      domainKey !== "test" &&
      domainKey !== "prodsup"
    ) {
      sessionStorage.setItem(DOMAIN_KEY, domainKey);
      return domainKey;
    }
  }

  // Check URL params for domainkey
  const urlParams = new URLSearchParams(window.location.search);
  let urlDomainKey = null;

  for (const [key, value] of urlParams.entries()) {
    if (key.toLowerCase() === URL_SEARCH_PARAMS.DomainKey) {
      urlDomainKey = value?.toLowerCase();
      break;
    }
  }

  if (urlDomainKey) {
    // Preserve query `domainkey` so bootstrap redirect logic can run before app render.
    sessionStorage.setItem(DOMAIN_KEY, urlDomainKey);
    return urlDomainKey;
  }

  return (
    sessionStorage.getItem(DOMAIN_KEY) ||
    process.env.REACT_APP_DOMAIN_KEY ||
    "evacuation"
  );
};

const getDomainKeyFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  for (const [key, value] of urlParams.entries()) {
    if (key.toLowerCase() === URL_SEARCH_PARAMS.DomainKey) {
      return value?.toLowerCase() || null;
    }
  }

  return null;
};

const normalizeConfiguredUrl = (configuredUrl) => {
  if (!configuredUrl) {
    return null;
  }

  const trimmedUrl = configuredUrl.trim();
  if (!trimmedUrl) {
    return null;
  }

  const normalizedWithScheme = /^https?:\/\//i.test(trimmedUrl)
    ? trimmedUrl
    : `https://${trimmedUrl}`;

  let parsedUrl;
  try {
    parsedUrl = new URL(normalizedWithScheme);
  } catch {
    return null;
  }

  if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
    return null;
  }

  if (parsedUrl.protocol === "http:" && !isLocalRedirectHost(parsedUrl.hostname)) {
    return null;
  }

  return parsedUrl.toString();
};

const combinePaths = (basePath, requestPath) => {
  const normalizedBasePath = !basePath || basePath === "/"
    ? ""
    : basePath.replace(/\/+$/, "");

  const normalizedRequestPath = !requestPath
    ? "/"
    : requestPath.startsWith("/") ? requestPath : `/${requestPath}`;

  if (!normalizedBasePath) {
    return normalizedRequestPath;
  }

  if (normalizedRequestPath === "/") {
    return normalizedBasePath;
  }

  return `${normalizedBasePath}${normalizedRequestPath}`;
};

const getQueryWithoutDomainKey = () => {
  const currentParams = new URLSearchParams(window.location.search);
  const filteredParams = new URLSearchParams();

  for (const [key, value] of currentParams.entries()) {
    if (key.toLowerCase() !== URL_SEARCH_PARAMS.DomainKey) {
      filteredParams.append(key, value);
    }
  }

  return filteredParams;
};

export const cleanupDomainKeyFromUrl = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const hasDomainKey = Array.from(new URLSearchParams(window.location.search).keys())
    .some((key) => key.toLowerCase() === URL_SEARCH_PARAMS.DomainKey);

  if (!hasDomainKey) {
    return false;
  }

  const filteredQuery = getQueryWithoutDomainKey();
  const cleanedUrl = `${window.location.pathname}${filteredQuery.toString() ? `?${filteredQuery.toString()}` : ""}${window.location.hash}`;
  window.history.replaceState(null, "", cleanedUrl);
  return true;
};

const parsePublicPortalUrl = (configuration) => {
  if (!configuration || typeof configuration !== "string") {
    return null;
  }

  try {
    const parsedConfiguration = JSON.parse(configuration);
    if (typeof parsedConfiguration === "string") {
      return parsedConfiguration;
    }

    if (parsedConfiguration && typeof parsedConfiguration.PublicPortalUrl === "string") {
      return parsedConfiguration.PublicPortalUrl;
    }
  } catch {
    return configuration;
  }

  return null;
};

const isAllowedRedirectHost = (host) => {
  if (!host) {
    return false;
  }

  const normalizedHost = host.toLowerCase();
  if (isLocalRedirectHost(normalizedHost)) {
    return true;
  }

  return ALLOWED_REDIRECT_HOST_SUFFIXES.some((suffix) => normalizedHost.endsWith(suffix));
};

export const bootstrapDomainKeyRedirect = async () => {
  if (typeof window === "undefined") {
    return false;
  }

  const urlDomainKey = getDomainKeyFromUrl();
  if (!urlDomainKey) {
    return false;
  }

  try {
    const headers = {
      [DOMAIN_KEY_HEADER_NAME]: urlDomainKey,
    };

    const programResponse = await fetch("/api/program", {
      method: "GET",
      headers,
      credentials: "include",
    });

    if (!programResponse.ok) {
      return false;
    }

    const program = await programResponse.json();
    if (!program?.id) {
      return false;
    }

    const settingsParams = new URLSearchParams({
      settingName: SettingNames.PublicPortalUrl,
      programId: program.id,
    });

    const programSettingResponse = await fetch(`/api/programSettings?${settingsParams.toString()}`, {
      method: "GET",
      headers,
      credentials: "include",
    });

    if (!programSettingResponse.ok) {
      return false;
    }

    const programSetting = await programSettingResponse.json();
    const configuredPortalUrl = parsePublicPortalUrl(programSetting?.configuration);
    const normalizedPortalUrl = normalizeConfiguredUrl(configuredPortalUrl);

    if (!normalizedPortalUrl) {
      return false;
    }

    const currentUrl = new URL(window.location.href);
    const redirectBaseUrl = new URL(normalizedPortalUrl);
    if (!isAllowedRedirectHost(redirectBaseUrl.hostname)) {
      return false;
    }

    const targetUrl = new URL(redirectBaseUrl.toString());

    targetUrl.pathname = combinePaths(redirectBaseUrl.pathname, currentUrl.pathname);

    const filteredQuery = getQueryWithoutDomainKey();
    targetUrl.search = filteredQuery.toString() ? `?${filteredQuery.toString()}` : "";

    const currentUrlWithoutDomainKey = new URL(currentUrl.toString());
    currentUrlWithoutDomainKey.search = filteredQuery.toString() ? `?${filteredQuery.toString()}` : "";

    const isSameTarget =
      currentUrlWithoutDomainKey.protocol.toLowerCase() === targetUrl.protocol.toLowerCase() &&
      currentUrlWithoutDomainKey.host.toLowerCase() === targetUrl.host.toLowerCase() &&
      currentUrlWithoutDomainKey.pathname === targetUrl.pathname &&
      currentUrlWithoutDomainKey.search === targetUrl.search;

    sessionStorage.setItem(DOMAIN_KEY, urlDomainKey);

    if (isSameTarget) {
      return false;
    }

    window.location.replace(targetUrl.toString());
    return true;
  } catch {
    return false;
  }
};

// Used to act like a getter, so that the domain key is up to date whenever it is accessed.
const domainKeyExport = {};
Object.defineProperty(domainKeyExport, "domainKey", {
  get: () => extractDomainKey(),
  enumerable: true,
});

export const { domainKey } = domainKeyExport;

export const refCategories = {
  EVENTS: "events",
  RELATIONSHIP: "relationship",
  GENDER: "gender",
  MONTHS: "months",
  DOCUMENT_TYPES: "documentTypes",
  APP_STATUS: "appStatus",
};

export const Relationship = {
  CHILD: "child",
  SPOUSE: "spouse",
  ADULT: "adult",
  ELDERLY: "elderly",
};

export const getBadgeType = (internalStatus) => {
  let badgeType = "information";
  if (internalStatus === InternalApplicationStatuses.Draft) {
    badgeType = "midtone";
  }
  if (internalStatus === InternalApplicationStatuses.FollowUp) {
    badgeType = "important";
  }
  if (
    internalStatus === InternalApplicationStatuses.Withdrawn ||
    internalStatus === InternalApplicationStatuses.Rejected
  ) {
    badgeType = "dark";
  }
  if (
    internalStatus === InternalApplicationStatuses.PaymentSent ||
    internalStatus === InternalApplicationStatuses.PaymentComplete
  ) {
    badgeType = "success";
  }

  return badgeType;
};

export const formatDate = (date) => {
  return date.toLocaleString("en-US", {
    timeZone: "America/Edmonton", // Adjusts for DST
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

export const EventsEnabled = (programFeatureMapping) => {
  if (!programFeatureMapping) {
    return undefined; // Return undefined when not loaded
  }

  return programFeatureMapping?.isEnabled ?? false;
};

export const isEventTypeActive = (programFeatureEventMapping, event, user) => {
  if (!programFeatureEventMapping && !event) {
    return undefined;
  }

  return (
    event.eventStatus !== EventStatuses.Draft &&
    (user?.isProxyUser || event.eventStatus === EventStatuses.Active)
  );
};

export const viewOnlyApplication = (internalStatus, user) => {
  return (
    !!internalStatus &&
    internalStatus !== InternalApplicationStatuses.Draft &&
    internalStatus !== InternalApplicationStatuses.Submitted &&
    internalStatus !== InternalApplicationStatuses.FollowUp &&
    (!user?.isProxyUser ||
      (internalStatus !== InternalApplicationStatuses.InProgress &&
       internalStatus !== InternalApplicationStatuses.PaymentComplete))
  );
};

// Remove hard coding for finish later and next steps.
// See MPP-4269
export const FINISH_LATER_FORM_ID = "finishLaterConfirmation";
export const NEXT_STEPS_FORM_ID = "nextSteps";

export const NoAvailDocumentTypeId = "1";

export const namePattern =
  "^[a-zA-ZÀ-ÖØ-öø-ÿĀ-žœŒŸçÇëËïÏûÛâÂêÊîÎôÔùÙàÀéÉèÈ'’.\\s-]*$";
export const emailPattern =
  "^$|[a-zA-Z0-9À-ÖØ-öø-ÿ._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$";
export const phonePattern =
  "^\\+\\d{11,17}$|^\\(\\d{3}\\)\\s*\\d{3}-?\\d{4}$|^\\d{3}\\s?\\d{3}-?\\d{4}$";

export const nameRegExp = new RegExp(namePattern);
export const phoneRegExp = new RegExp(phonePattern);
export const phoneExtRegExp = /^\d+$/;
export const postalCodeRegExp = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
export const streetNameRegExp = /^[a-zA-Z0-9\s#,'.\-\/ 'àâäçéèêëîïôöùûüÿœæÉ]*$/;
export const cityNameRegExp = /^[a-zA-Z0-9àâäçéèêëîïôöùûüÿœæÉ\s#,'.\-\/]*$/;
export const bandRegExp = /^\d{3}$/;
export const familyRegExp = /^\d{5}$/;
export const positionRegExp = /^\d{2}$/;
export const fnibRegExp = /^\d{8}$/;
export const multipleSpacesRegExp = /\s+/g;
export const poBoxRegExp = /\bp\.?\s*[o0]\.?\s*box\b/i;

// Helper function to normalize name fields (trim, replace multiple spaces, lowercase)
export const normalizeName = (name) => {
  return (name || '').toLowerCase().trim().replace(multipleSpacesRegExp, ' ');
};

export const formatPhoneNumber = (phoneNumber) => {
  // Remove non-digit characters
  const digitsOnly = phoneNumber.replace(/[^\d+]/g, "");

  // Check if the phone number starts with '+'
  if (digitsOnly.startsWith("+")) {
    return digitsOnly;
  }

  // Add parentheses and hyphen for phone numbers without '+'
  if (digitsOnly?.length >= 10) {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(
      3,
      6,
    )}-${digitsOnly.slice(6, 10)}`;
  }

  return digitsOnly;
};

export const toTitleCase = (str) => {
  if (str) {
    return str.replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  }
};

export const formatPostalCode = (postalCode) => {
  return (postalCode ?? "")
    .replace(/\s/g, "") // Remove whitespace
    .slice(0, 6) // Limit input to 6 characters (e.g., A1A1A1)
    .replace(/([A-Za-z]\d[A-Za-z])(\d[A-Za-z]\d)/, "$1 $2")
    .toUpperCase(); // Format as A1A 1A1
};

export const getGenderFromKey = (key) => {
  const gender = genderList.find((x) => x.key === key);

  return gender?.label || "";
};

export const genderList = [
  { key: 0, label: "Unspecified" },
  { key: 1, label: "Male" },
  { key: 2, label: "Female" },
];

export function getFullName(givenName, middleName, surname) {
  return `${givenName} ${middleName ? middleName + " " : ""}${surname}`;
}

export const provinceList = [
  { label: "Alberta", code: "CA-AB" },
  { label: "British Columbia", code: "CA-BC" },
  { label: "Manitoba", code: "CA-MB" },
  { label: "Newfoundland and Labrador", code: "CA-NL" },
  { label: "Nova Scotia", code: "CA-NS" },
  { label: "New Brunswick", code: "CA-NB" },
  { label: "Ontario", code: "CA-ON" },
  { label: "Prince Edward Island", code: "CA-PE" },
  { label: "Quebec", code: "CA-QC" },
  { label: "Saskatchewan", code: "CA-SK" },
  { label: "Yukon", code: "CA-YT" },
  { label: "Northwest Territories", code: "CA-NT" },
  { label: "Nunavut", code: "CA-NU" },
];

export function getProvinceName(code) {
  const province = provinceList.find((x) => x.code == code);
  return province?.label || " ";
}

export function getFullCountryName(code) {
  if (code === "CA") {
    return "Canada";
  }

  if (code === "US" || code === "USA") {
    return "United States of America";
  }
  return code;
}

export const getAddressDisplay = (
  streetAddressLine1,
  streetAddressLine2,
  locality,
  province,
  postalCode,
  country,
) => {
  return (
    <div>
      <div>{streetAddressLine1}</div>
      {streetAddressLine2 && <div>{streetAddressLine2}</div>}
      <div>{`${locality}, ${getProvinceName(
        province,
      )}${"\u00A0\u00A0"}${postalCode}${
        country ? ` ${getFullCountryName(country)}` : ""
      }`}</div>
    </div>
  );
};

export const getPhoneDisplay = (phone, phoneExtensionLabel, phoneExtension) => {
  if (!phone && !phoneExtension) {
    return "";
  }
  return (
    <>
      {phone}{" "}
      {phoneExtension && (
        <span>
          {phoneExtensionLabel} {phoneExtension}
        </span>
      )}
    </>
  );
};

export const onlyNumberKey = (id) => {
  document.getElementById(id)?.addEventListener("keypress", (event) => {
    if (isNaN(event.key)) {
      event.stopImmediatePropagation();
      event.preventDefault();
    }
  });
};

export const validatePhoneInputField = (e) => {
  const key = e.data;
  const inputType = e.inputType;
  const inputField = e.target;

  // Check for system keys (like Backspace, Delete, Arrow keys, etc.)
  const systemKeys = [
    "deleteContentBackward",
    "Backspace",
    "Tab",
    "ArrowLeft",
    "ArrowRight",
    "Delete",
    "Enter",
    "Tab",
  ];
  if (systemKeys.includes(inputType)) {
    return;
  }

  // Allow only numbers
  const isNumber = /^[0-9]$/.test(key);
  // Allow only "+" at the beginning
  const isPlus = key === "+";
  const currentValue = inputField.value ?? "";
  const isFirstChar = currentValue?.length === 0;
  const effectiveLength = currentValue?.length;

  // Check if the input is valid so far
  const isValidInput = isNumber || (isPlus && isFirstChar);

  // Check if max length is reached
  const maxDigits = currentValue.startsWith("+") ? 18 : 10;
  const isMaxLengthExceeded = effectiveLength >= maxDigits;

  if (isValidInput && !isMaxLengthExceeded) {
    // Allow the key press
    return;
  }

  // Prevent key press
  e.stopImmediatePropagation();
  e.preventDefault();
};

export const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const getDateObj = (date) => {
  if (date === null || date === undefined || date === "") {
    return null;
  }

  try {
    // If it's already a Date object, return it
    if (date instanceof Date) {
      return date;
    }

    // Convert from date string to date obj
    if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split("-").map(Number);
      return new Date(year, month - 1, day);
    }

    return new Date(date);
  } catch (e) {
    return null;
  }
};

export const getDate = (date) => {
  const dateObj = getDateObj(date);
  return !dateObj
    ? ""
    : `${dateObj.getDate()} ${months[dateObj.getMonth()]} ${dateObj.getFullYear()}`;
};

export const formatDateForDatabase = (date) => {
  if (date === null || date === undefined || date === "") {
    return null;
  }

  const dateObj = getDateObj(date);
  if (!dateObj) {
    return null;
  }

  // Format as YYYY-MM-DD (ISO date format without time)
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const formatDateOfBirth = (date) => {
  // Specifically for date of birth - ensures consistent date-only format
  // This is used when saving DOB data to applicationDataJson
  return formatDateForDatabase(date);
};

export const evaluate = (condition, context, form) => {
  if (condition?.expression) {
    try {
      if (condition.context === "user") {
        return jexl.evalSync(condition.expression, context.user);
      }
      if (condition.context === "form") {
        return jexl.evalSync(condition.expression, form);
      }
      if (condition.context) {
        const stepData = context.getStepData(condition.context);
        if (stepData) {
          return jexl.evalSync(condition.expression, stepData);
        }
      }
      if (condition.isEmpty !== undefined) {
        let numOfItems = 0;
        let hasAllDefaultValues = false;
        if (condition.category && context[condition.category]) {
          numOfItems = context[condition.category].length;
        } else if (context[condition.expression]) {
          numOfItems = Object.keys(context[condition.expression]).length;
          if (numOfItems > 0) {
            hasAllDefaultValues = Object.values(
              context[condition.expression],
            ).every((value) =>
              typeof value === "string"
                ? value === ""
                : typeof value === "number"
                  ? value === 0
                  : typeof value === "boolean"
                    ? value === false
                    : value === null || value === undefined,
            );
            if (context[condition.expression]?.isInternal) {
              hasAllDefaultValues = true;
            }
          }
        }
        const result =
          (condition.isEmpty && (!numOfItems || hasAllDefaultValues)) ||
          (!condition.isEmpty && !!numOfItems && !hasAllDefaultValues);
        if (
          (condition.operator === "or" && result) ||
          (condition.operator === "and" && !result) ||
          !condition?.category ||
          condition?.expression === condition?.category
        ) {
          return result;
        }
      }
      if (
        condition.stepData !== "context" &&
        context.stepData &&
        Object.keys(context.stepData)?.length &&
        !condition.stepName
      ) {
        return jexl.evalSync(condition.expression, context.stepData);
      }

      return jexl.evalSync(condition.expression, context);
    } catch (ex) {
      console.error(
        `Exception during evaluating: ${condition?.expression}. Error: ${ex.message}`,
      );
      return false;
    }
  }
  return false;
};

export const tryParse = (json) => {
  try {
    return JSON.parse(json);
  } catch {
    return {};
  }
};

export const calculateAge = (dob) => {
  if (!dob) return null;

  // If dob is a string, convert to JS Date first
  const jsDate = getDateObj(dob);

  const birth = DateTime.fromJSDate(jsDate).startOf('day');

  if (!birth.isValid) return null;
  const age = Math.floor(DateTime.now().startOf('day').diff(birth, 'years').years);
  return age;
};


export const getPaymentMethod = (receivePayment, isProxyUser = "false") => {
  if (receivePayment === "prepaid") {
    if (isProxyUser) {
      return "Prepaid card";
    } else {
      return 'Visit <a target="_blank" href="https://www.alberta.ca/alberta-supports">Alberta Supports Centre</a> in-person for payment';
    }
  }

  return "e-Transfer";
};

export const getETransferMethod = (receivePayment, receiveETransfer) => {
  if (receivePayment === "e-transfer") {
    if (receiveETransfer === "mobile") {
      return "Mobile phone number";
    }
    if (receiveETransfer === "bankAccount") {
      return "Using bank account details";
    }
    return "Email address";
  }

  return "N/A";
};

export const isPortalActive = (portal, user) => {
  return (
    portal?.programStatus !== ProgramStatuses.Draft &&
    portal?.programStatus !== ProgramStatuses.Inactive &&
    (user?.isProxyUser || portal?.isPublicPortalOpen)
  );
};

//Setter
export const setProxySessionId = (id) => {
  if (id) {
    localStorage.setItem(PROXY_SESSION_ID_KEY, id);
  } else {
    localStorage.removeItem(PROXY_SESSION_ID_KEY);
  }
};

// 2. Getter (Simple Read)
export const getProxySessionId = () => {
  return localStorage.getItem(PROXY_SESSION_ID_KEY);
};

// 3. Initializer 
export const initProxySession = () => {
  // ensure window exists
  if (typeof window === 'undefined') return;

  // A. Check URL
  const urlParams = new URLSearchParams(window.location.search);
  const urlSessionId = urlParams.get('proxySessionId');

  if (urlSessionId) {
    
    // B. Save to Storage
    setProxySessionId(urlSessionId);

    // C. Clean URL
    urlParams.delete('proxySessionId');
    const newSearch = urlParams.toString();
    const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '');
    
    // Wrap history update in a small timeout to prevent blocking the render
    setTimeout(() => {
        window.history.replaceState(null, '', newUrl);
    }, 0);
  }
};

